import React from 'react';
import { useSocialContext } from './social';

// El frontend NO decide quién es admin: le pregunta al backend (/drupi/admin/whoami),
// que deriva la identidad del JWT (validado por OAuth) y la compara contra la lista
// hardcodeada del server (shared/drupi/admin.py). Una sola fuente de verdad.
const JWT_KEY  = 'drupi_social_jwt';
const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

interface AdminState {
  isAdmin: boolean;
  loading: boolean;
}

const AdminContext = React.createContext<AdminState>({ isAdmin: false, loading: true });

export const useAdmin = (): AdminState => React.useContext(AdminContext);

export const AdminProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  // Re-chequeamos cuando la auth social termina de resolverse (el JWT puede
  // setearse durante el callback de OAuth, después del primer render).
  const { isAuthenticated, loading: socialLoading } = useSocialContext();
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    const jwt = localStorage.getItem(JWT_KEY);

    if (!jwt || !isAuthenticated) {
      // No logueado (todavía). Mantenemos loading hasta que la auth social asiente.
      if (!socialLoading) {
        setIsAdmin(false);
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    fetch(`${API_BASE}/drupi/admin/whoami`, {
      headers: { Authorization: `Bearer ${jwt}` },
    })
      .then(r => (r.ok ? r.json() : { is_admin: false }))
      .then(d => { if (!cancelled) setIsAdmin(!!d.is_admin); })
      .catch(() => { if (!cancelled) setIsAdmin(false); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [isAuthenticated, socialLoading]);

  return (
    <AdminContext.Provider value={{ isAdmin, loading }}>
      {children}
    </AdminContext.Provider>
  );
};
